// Fetch wrapper. Sets X-Requested-With: fetch on every mutating request so
// SPEC §15.9.3 CSRF defense is satisfied; same-origin only via CORS in §15.9.7.
// `credentials: 'include'` ensures the session cookie travels.
export interface ApiRequestInit extends Omit<RequestInit, 'body'> {
  body?: unknown;
}

export class ApiError extends Error {
  constructor(
    public status: number,
    public body: unknown,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export async function api<T = unknown>(path: string, init: ApiRequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  headers.set('X-Requested-With', 'fetch');
  if (init.body !== undefined && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  const { body: _bodyDrop, ...rest } = init;
  void _bodyDrop;
  const requestInit: RequestInit = {
    ...rest,
    headers,
    credentials: 'include',
  };
  if (init.body !== undefined) {
    requestInit.body = typeof init.body === 'string' ? init.body : JSON.stringify(init.body);
  }

  const res = await fetch(path.startsWith('/') ? path : `/api/${path}`, requestInit);

  if (!res.ok) {
    let body: unknown = null;
    try {
      body = await res.json();
    } catch {
      // ignore — body may not be JSON
    }
    throw new ApiError(res.status, body, `${String(res.status)} ${res.statusText}`);
  }

  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}
